import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ESCALATION_RECIPIENTS, SLA_SCOPES, updateSlaRuleSchema, type EscalationRecipientKind, type SlaRuleView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useSlaRules, useUpdateSlaRule } from '../api/requisitions-api';
import { formatActive } from '../sla-format';
import styles from './TimeLimitsPage.module.css';

/**
 * Admin · Time limits (F16.08), its own page since the Admin redesign (2026-11-02, option A of the "Time limits" tab in
 * `4-messages/approval-chains.html`), gated only on Time limits (`sla`): approvals run without Approval chains, so their limits must be
 * editable without it. It edits the two limits that exist: each approval step (the HR check and every chain step alike: per-step
 * limits were dropped), and approved → job created. Each in working hours, with its warning mark and escalation rungs, and a bar
 * that draws those numbers (a picture, not a setting).
 *
 * Two Admins at once: each limit's draft keeps the version it started from, so a save over someone else's is refused (409), and
 * the card says so with "Load theirs" instead of overwriting them (wave 1 review W7).
 */

const SCOPE_LABELS: Readonly<Record<string, string>> = {
  [SLA_SCOPES.APPROVAL]: 'Each approval step',
  [SLA_SCOPES.APPROVED]: 'Approved → job created',
};

const SCOPE_HINTS: Readonly<Record<string, string>> = {
  [SLA_SCOPES.APPROVAL]: "From the moment a step is waiting until it is decided: HR's check and every step after it.",
  [SLA_SCOPES.APPROVED]: 'From the last approval until the job exists.',
};

const RECIPIENT_LABELS: Readonly<Record<EscalationRecipientKind, string>> = {
  manager: "Tell the waiting person's manager",
  hr_lead: 'Tell HR',
};

const MAX_RUNGS = 5;

/** What the fields hold: text, so a half-typed number is kept as typed. */
interface RuleValues {
  readonly hours: string;
  readonly warning: string;
  readonly rungs: readonly { readonly atPercent: string; readonly recipient: EscalationRecipientKind }[];
}

interface RuleEdit {
  /** The limit as it was when editing started; its version is the one Save sends. */
  readonly base: SlaRuleView;
  readonly values: RuleValues;
}

function valuesOf(rule: SlaRuleView): RuleValues {
  return {
    hours: String(rule.targetWorkingHours),
    warning: String(rule.warningAtPercent),
    rungs: rule.escalationChain.map((rung) => ({ atPercent: String(rung.atPercent), recipient: rung.recipient })),
  };
}

function sameValues(a: RuleValues, b: RuleValues): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Only whole numbers are numbers here; anything else is sent as NaN and refused with the field's own message. */
function wholeNumber(text: string): number {
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : Number.NaN;
}

/** The body for PATCH /admin/sla-rules/:id, checked by the same schema the server uses; field messages by name. */
export function checkRule(version: number, values: RuleValues) {
  const parsed = updateSlaRuleSchema.safeParse({
    version,
    targetWorkingHours: wholeNumber(values.hours),
    warningAtPercent: wholeNumber(values.warning),
    escalationChain: values.rungs.map((rung) => ({ atPercent: wholeNumber(rung.atPercent), recipient: rung.recipient })),
  });
  if (parsed.success) return { body: parsed.data, errors: {} as Readonly<Record<string, string>> };
  const errors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path.join('.');
    errors[key] ??= key === 'targetWorkingHours' ? 'Whole working hours, 1 to 2000.' : key === 'warningAtPercent' ? 'Between 50% and 99%.' : issue.message.startsWith('An escalation') ? issue.message : 'Between 101% and 1000%.';
  }
  return { body: null, errors };
}

export function TimeLimitsPage(): ReactNode {
  const canView = useCanView('admin_config');
  const canEdit = useCanEdit('admin_config');
  const rules = useSlaRules(canView);
  const update = useUpdateSlaRule();
  const show = useToasts((state) => state.show);
  const [edits, setEdits] = useState<Readonly<Record<string, RuleEdit>>>({});
  const [isSaving, setIsSaving] = useState(false);
  useUnsavedWarning(Object.keys(edits).length > 0);

  if (!canView) return <ForbiddenState resource="time limits" contact="Your admin" />;
  if (rules.isPending) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={8} />
      </div>
    );
  }
  if (rules.isError) {
    return (
      <div className={styles.page}>
        <ServerErrorState requestId={rules.error.requestId} onRetry={() => void rules.refetch()} />
      </div>
    );
  }

  const list = rules.data;
  const pending = Object.values(edits);
  const isStale = (edit: RuleEdit): boolean => list.find((rule) => rule.id === edit.base.id)?.version !== edit.base.version;
  const hasProblems = pending.some((edit) => checkRule(edit.base.version, edit.values).body === null);
  const staleCount = isSaving ? 0 : pending.filter(isStale).length;

  const setValues = (rule: SlaRuleView, values: RuleValues): void =>
    setEdits((current) => {
      const base = current[rule.id]?.base ?? rule;
      const next = { ...current };
      if (sameValues(valuesOf(base), values)) delete next[rule.id];
      else next[rule.id] = { base, values };
      return next;
    });
  const drop = (id: string): void =>
    setEdits((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });

  async function saveAll(): Promise<void> {
    setIsSaving(true);
    let saved = 0;
    try {
      for (const edit of pending) {
        const { body } = checkRule(edit.base.version, edit.values);
        if (!body) continue;
        try {
          await update.mutateAsync({ id: edit.base.id, ...body });
          drop(edit.base.id);
          saved += 1;
        } catch (cause) {
          const label = SCOPE_LABELS[edit.base.scopeKey] ?? edit.base.name;
          const isConflict = cause instanceof ApiError && cause.status === 409;
          show({
            tone: 'danger',
            message: `${label} was not saved`,
            detail: isConflict ? 'Someone else changed it meanwhile. Your numbers are kept on the card; load theirs to start again from them.' : errorMessage(cause),
          });
          return;
        }
      }
      if (saved > 0) {
        show({ tone: 'success', message: saved === 1 ? 'Time limit saved' : 'Time limits saved', detail: 'A new limit counts for clocks that start from now; warnings and escalations follow the new marks at once.' });
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Time limits</h1>
        <p className={styles.lede}>
          How long a hire request may wait, in working hours of its calendar. It shows a warning at the warn mark, is overdue past the
          limit, and each escalation tells someone. Every approval step has the same limit, HR's check and each chain step alike.
        </p>
      </header>

      {list.length === 0 ? (
        <EmptyState title="No time limits yet" body="The standard limits are added when the app is set up. Ask your developer to run the set-up again." action={null} />
      ) : (
        <div className={styles.rules}>
          {list.map((rule) => {
            const edit = edits[rule.id];
            return (
              <RuleCard
                key={rule.id}
                rule={rule}
                values={edit?.values ?? valuesOf(rule)}
                isStale={!isSaving && edit !== undefined && isStale(edit)}
                canEdit={canEdit}
                onChange={(values) => setValues(rule, values)}
                onLoadTheirs={() => drop(rule.id)}
              />
            );
          })}
        </div>
      )}

      {canEdit && pending.length > 0 && (
        <div className={styles.saveBar} role="region" aria-label="Unsaved time limits">
          <span className={styles.saveNote}>
            {staleCount > 0
              ? 'Someone else changed a limit you are editing. Load theirs on that card first.'
              : hasProblems
                ? 'Fix the marked numbers first.'
                : `${pending.length} ${pending.length === 1 ? 'limit' : 'limits'} changed`}
          </span>
          <Button variant="ghost" disabled={isSaving} onClick={() => setEdits({})}>
            Discard changes
          </Button>
          <Button variant="solid" disabled={hasProblems || staleCount > 0} isPending={isSaving} onClick={() => void saveAll()}>
            Save time limits
          </Button>
        </div>
      )}
    </div>
  );
}

function RuleCard({
  rule,
  values,
  isStale,
  canEdit,
  onChange,
  onLoadTheirs,
}: {
  readonly rule: SlaRuleView;
  readonly values: RuleValues;
  readonly isStale: boolean;
  readonly canEdit: boolean;
  readonly onChange: (values: RuleValues) => void;
  readonly onLoadTheirs: () => void;
}): ReactNode {
  const { errors } = checkRule(rule.version, values);
  const label = SCOPE_LABELS[rule.scopeKey] ?? rule.name;
  const readOnly = !canEdit;
  const setRung = (index: number, patch: Partial<RuleValues['rungs'][number]>): void =>
    onChange({ ...values, rungs: values.rungs.map((rung, i) => (i === index ? { ...rung, ...patch } : rung)) });
  return (
    <Card title={label} meta={`${rule.ventureName ?? 'Every venture'} · now ${formatActive(rule.targetWorkingHours * 60)}`}>
      <div className={styles.body}>
        {SCOPE_HINTS[rule.scopeKey] && <p className={styles.hint}>{SCOPE_HINTS[rule.scopeKey]}</p>}
        {isStale && (
          <p className={styles.stale} role="status">
            Someone else changed this limit while you were editing it.{' '}
            <Button size="sm" variant="outline" onClick={onLoadTheirs}>
              Load theirs
            </Button>
          </p>
        )}
        <div className={styles.fields}>
          <Field label="Limit" hint="Working hours. A working day is nine." error={errors.targetWorkingHours}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isNumeric
                inputMode="numeric"
                suffix="h"
                disabled={readOnly}
                isInvalid={errors.targetWorkingHours !== undefined}
                value={values.hours}
                onChange={(event) => onChange({ ...values, hours: event.target.value })}
              />
            )}
          </Field>
          <Field label="Warn at" error={errors.warningAtPercent}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isNumeric
                inputMode="numeric"
                suffix="%"
                disabled={readOnly}
                isInvalid={errors.warningAtPercent !== undefined}
                value={values.warning}
                onChange={(event) => onChange({ ...values, warning: event.target.value })}
              />
            )}
          </Field>
          <div className={styles.rungs}>
            <span className={styles.rungsLabel}>Escalate</span>
            {values.rungs.length === 0 && <span className={styles.hint}>Nobody is told when it runs over.</span>}
            {values.rungs.map((rung, index) => {
              const error = errors[`escalationChain.${index}.atPercent`] ?? errors[`escalationChain.${index}`];
              return (
                <div key={index} className={styles.rung}>
                  <Input
                    aria-label={`Escalation ${index + 1}, at`}
                    isNumeric
                    inputMode="numeric"
                    suffix="%"
                    disabled={readOnly}
                    isInvalid={error !== undefined}
                    value={rung.atPercent}
                    onChange={(event) => setRung(index, { atPercent: event.target.value })}
                  />
                  <Select
                    label={`Escalation ${index + 1}, who is told`}
                    disabled={readOnly}
                    value={rung.recipient}
                    options={ESCALATION_RECIPIENTS.map((value) => ({ value, label: RECIPIENT_LABELS[value] }))}
                    onChange={(value) => setRung(index, { recipient: value === 'hr_lead' ? 'hr_lead' : 'manager' })}
                  />
                  {!readOnly && (
                    <Button size="sm" variant="ghost" icon={Trash2} onClick={() => onChange({ ...values, rungs: values.rungs.filter((_, i) => i !== index) })}>
                      Remove
                    </Button>
                  )}
                  {error && <span className={styles.error}>{error}</span>}
                </div>
              );
            })}
            {!readOnly && values.rungs.length < MAX_RUNGS && (
              <Button
                size="sm"
                variant="ghost"
                icon={Plus}
                onClick={() => onChange({ ...values, rungs: [...values.rungs, { atPercent: String(nextRung(values)), recipient: 'manager' }] })}
              >
                Add an escalation
              </Button>
            )}
          </div>
        </div>
        <LimitBar values={values} />
      </div>
    </Card>
  );
}

/** A new rung goes 50% past the last one (150% when there is none), never past the 1000% the server allows. */
function nextRung(values: RuleValues): number {
  const last = Math.max(100, ...values.rungs.map((rung) => wholeNumber(rung.atPercent)).filter(Number.isFinite));
  return Math.min(1000, last + 50);
}

/** The numbers as a bar: on track up to the warning, due soon up to the limit, overdue to the last escalation. A picture only. */
function LimitBar({ values }: { readonly values: RuleValues }): ReactNode {
  const hours = wholeNumber(values.hours);
  const warning = wholeNumber(values.warning);
  if (!Number.isFinite(hours) || !Number.isFinite(warning) || warning <= 0 || warning >= 100) return null;
  const rungs = values.rungs.map((rung) => wholeNumber(rung.atPercent)).filter((at) => Number.isFinite(at) && at > 100).sort((a, b) => a - b);
  const last = rungs.at(-1) ?? 150;
  const at = (percent: number): string => formatActive(Math.round((hours * 60 * percent) / 100));
  return (
    <figure className={styles.bar} aria-label={`On track until ${warning}%, due soon until 100%, then overdue`}>
      <div className={styles.track} aria-hidden>
        <span className={styles.onTrack} style={{ flexGrow: warning }} />
        <span className={styles.dueSoon} style={{ flexGrow: 100 - warning }} />
        <span className={styles.overdue} style={{ flexGrow: last - 100 }} />
      </div>
      <figcaption className={styles.marks}>
        <span>Start</span>
        <span>
          Warning at {warning}% · {at(warning)}
        </span>
        <span>Overdue at 100% · {at(100)}</span>
        {rungs.map((rung, index) => (
          <span key={`${rung}-${index}`}>
            Escalation at {rung}% · {at(rung)}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
